import { useEffect, useState } from 'react'

// Router
import { useParams, useNavigate } from 'react-router-dom'

// Services
import { useGetSurgeryQuery } from 'lib/apollo/hooks'
import { useDisablePolling } from 'lib/useDisablePolling'
import { useGetPreferenceCardById } from 'lib/services/api/reports-service/preference-card/getPreferenceCardById'

// Types
import { SurgeryResult } from 'views/PreferenceCards/PreferenceCards.types'
import { PreferenceCardProps } from 'views/PreferenceCards/CardView/CardView.types'

// MUI
import { Grid } from '@mui/material'
import { Item } from './PreferenceCard.types'
import useFindProductByIds from 'lib/services/api/product-service/findProductsByIds'
import { BetterIdAsset } from 'common/types'
import { useUnAssignPreferenceCard } from 'lib/services/api/reports-service/preference-card/unAssignPreferenceCard'
import { enqueueSnackbar } from 'notistack'
import { usePreferenceCard } from 'lib/context/PreferenceCardActionContext/PreferenceCardActionContext'

const usePreferenceCardLogic = () => {
  const navigate = useNavigate()
  const { setPreferenceCardAction } = usePreferenceCard()
  const { surgeryId } = useParams<{ surgeryId: string }>()
  const [preferenceCard, setPreferenceCard] = useState<
    PreferenceCardProps | undefined
  >()
  const [accordionsToggle, setAccordionsToggle] = useState({
    procedureInformation: true,
    equipment: true,
    instrumentTrays: true,
    implants: true,
    biologics: true,
    anesthesia: true,
    consumables: true,
    linkedSalesReps: true,
  })

  // Services
  const disablePolling = useDisablePolling()
  const { data, loading: isLoadingSurgery } = useGetSurgeryQuery(surgeryId, {
    disablePolling,
  })
  const result: SurgeryResult = data?.getSurgery
  const preferenceCardId = result?.preferenceCardId

  const { data: cardData, isLoading: isLoadingCard } = useGetPreferenceCardById(
    {
      id: preferenceCardId,
      enabled: Boolean(preferenceCardId),
    }
  )

  const {
    data: consumablesProductsData,
    isLoading: isLoadingConsumablesProductsData,
  } = useFindProductByIds(
    preferenceCard?.consumables?.items?.map((item) => item.bidProductId) || []
  )

  const { mutateAsync: unAssignPrefCard, isLoading: isUnAssigning } =
    useUnAssignPreferenceCard()

  const products = consumablesProductsData?.data.products
  const consumables = preferenceCard?.consumables?.items?.map((i) => ({
    bidProductId: i.bidProductId,
    qty: i.qty,
    item: products?.find((p) => p.id === i.bidProductId) as BetterIdAsset,
    tags: i.tags,
  }))

  const filteredPositioningDevices =
    preferenceCard?.procedureDetails?.positioningDevices &&
    Object.entries(preferenceCard.procedureDetails.positioningDevices).filter(
      ([key, value]) => value
    )

  const filteredSkinPreps =
    preferenceCard?.procedureDetails?.skinPrep &&
    Object.entries(preferenceCard.procedureDetails.skinPrep).filter(
      ([key, value]) => value
    )

  const filteredAnesthesia =
    preferenceCard?.procedureDetails?.anesthesia &&
    Object.entries(preferenceCard.procedureDetails.anesthesia).filter(
      ([key, value]) => value
    )

  const flattenedArray = preferenceCard?.equipment?.options
    ?.filter(Boolean)
    .flatMap((equipment) =>
      Object.entries(equipment).map(([key, value]) => ({ [key]: value }))
    )

  const formatOutput = (
    key: string,
    value: string | number | boolean
  ): string | null => {
    const isBoolean = typeof value === 'boolean'
    const isText = typeof value === 'string' || typeof value === 'number'

    if (isText && !value.toString().length) return null
    if (key === 'Side' || key === 'Degree') return null

    if (isBoolean) {
      return value ? 'Yes' : 'No'
    }

    if (isText) {
      return value ? value.toString() : 'None'
    }

    return null
  }

  const GridItem: React.FC<{ item: Item }> = ({ item }) => (
    <>
      {Object.entries(item).map(([key, value]) => {
        const output = formatOutput(key, value)
        if (output === null) return null

        return (
          <div className="item" key={key}>
            <div className="label">{key}</div>
            <div className="value">{output}</div>
          </div>
        )
      })}
    </>
  )

  const renderGridItems = (flattenedArray: Item[]): React.ReactNode => (
    <>
      {flattenedArray.map((item, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <GridItem item={item} />
        </Grid>
      ))}
    </>
  )

  const handleEditPreferenceCard = (id?: string) => {
    if (id) {
      navigate(`/preference-card/${id}`, {
        state: {
          returnToSurgery: true,
          surgeryId: surgeryId,
        },
      })
    }
  }

  const handleUnassignCard = async () => {
    try {
      await unAssignPrefCard({ surgeryId: surgeryId as string })
      navigate(`/surgeries/${surgeryId}`)
      setPreferenceCardAction('unassign')
    } catch (error) {
      enqueueSnackbar('Failed to unassign preference card.', {
        variant: 'error',
      })
      console.error(error)
    }
  }

  useEffect(() => {
    if (cardData?.card_details) {
      setPreferenceCard({
        ...cardData?.card_details,
        ...cardData,
      })
    }
  }, [cardData])

  return {
    cardData,
    consumables,
    isLoading: isLoadingCard || isLoadingSurgery,
    preferenceCard,
    isUnAssigning,
    flattenedArray,
    accordionsToggle,
    filteredSkinPreps,
    filteredPositioningDevices,
    filteredAnesthesia,
    isLoadingConsumablesProductsData,
    renderGridItems,
    setAccordionsToggle,
    handleEditPreferenceCard,
    handleUnassignCard,
  }
}

export default usePreferenceCardLogic
